import { Box, Typography } from '@mui/material';
import ProtectedEmail from '../../../../../ui/protectedEmail';
import PolicyContentSection, {
  SectionTitle,
  SectionContent,
  SubsectionText,
} from '../../../shared/components/PolicyContentSection';

const ContentSection = () => {
  return (
    <PolicyContentSection
      title="Terms & Conditions"
      lastUpdated="Last updated: December 17th, 2025"
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
          (For general use of the Nirva website and online content)
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary' }}
        >
          These Website Terms & Conditions ("T&Cs") apply to all visitors and users of mynirva.com
          and any related pages, whether or not you become a client or practitioner.
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mt: 1 }}
        >
          By accessing or using our website, you agree to these T&Cs. If you do not agree, please do
          not use the site.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>1. Informational Use Only</SectionTitle>
        <SectionContent>The information on this website is:</SectionContent>
        <SubsectionText>• For general educational and informational purposes only</SubsectionText>
        <SubsectionText>
          • Not a substitute for medical, psychological, psychiatric, or other professional advice
        </SubsectionText>
        <SubsectionText>• Not intended to diagnose, treat, or cure any condition</SubsectionText>
        <SubsectionText>
          • Always seek the advice of qualified healthcare or mental health professionals regarding
          questions about diagnosis, treatment, or medications.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>2. No Emergency or Crisis Support</SectionTitle>
        <SectionContent>
          This website is not monitored for emergencies and may not be checked in real time. If you
          are in crisis, or believe you may harm yourself or others:
        </SectionContent>
        <SubsectionText>• Call 911 (or your local emergency number), or</SubsectionText>
        <SubsectionText>• Go to the nearest emergency room</SubsectionText>
        <SubsectionText>
          • Do not use this website or email for urgent or life-threatening situations.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>3. Intellectual Property</SectionTitle>
        <SectionContent>
          All content on this website—including logos, images, graphics, text, videos, and audio—is
          owned by Nirva or used with permission.
        </SectionContent>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 2, mb: 1 }}>
          You may:
        </Typography>
        <SubsectionText>
          • View and download content for your personal, non-commercial use
        </SubsectionText>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 2, mb: 1 }}>
          You may not:
        </Typography>
        <SubsectionText>
          • Copy, modify, distribute, reuse, or sell any content without written permission
        </SubsectionText>
        <SubsectionText>
          • Use Nirva's name, logo, or branding in a way that implies endorsement without our
          consent
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>4. User Conduct</SectionTitle>
        <SectionContent>You agree not to:</SectionContent>
        <SubsectionText>• Use the site for unlawful, harmful, or abusive purposes</SubsectionText>
        <SubsectionText>
          • Attempt to disrupt or interfere with site security or operation
        </SubsectionText>
        <SubsectionText>• Upload or transmit viruses or malicious code</SubsectionText>
        <SubsectionText>• Attempt to access restricted areas without authorization</SubsectionText>
        <SubsectionText>
          • We may restrict or block access if we believe your conduct violates these T&Cs, our
          policies, or applicable law.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>5. Links to Other Sites</SectionTitle>
        <SectionContent>
          Our website may link to third-party sites (e.g., resources, tools, or partners). Nirva is
          not responsible for the content, security, or privacy practices of those sites. We
          encourage you to review their terms and privacy policies.
        </SectionContent>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>6. Disclaimers</SectionTitle>
        <SectionContent>
          The website and all content are provided "as is" and "as available" without warranties of
          any kind, express or implied, including but not limited to fitness for a particular
          purpose, accuracy, or non-infringement.
        </SectionContent>
        <SubsectionText>
          • We make reasonable efforts to keep the information current, but we do not guarantee that
          all information is always complete, accurate, or up-to-date.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>7. Limitation of Liability</SectionTitle>
        <SectionContent>
          To the fullest extent allowed by law, Nirva and its owners, staff, and practitioners are
          not liable for any indirect, incidental, consequential, or special damages arising from
          your use of this website, even if advised of the possibility of such damages.
        </SectionContent>
        <SubsectionText>
          • If any liability is found, it will be limited to the amount, if any, that you paid
          directly to Nirva for access to the site or specific digital content.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>8. Changes to These T&Cs</SectionTitle>
        <SectionContent>
          We may revise these T&Cs from time to time. Updated versions will be posted on this page
          with a new "Last updated" date. Continued use of the site after changes means you accept
          the updated T&Cs.
        </SectionContent>
      </Box>

      <Box sx={{ mb: 6 }}>
        <SectionTitle>9. Contact</SectionTitle>
        <SectionContent>
          For questions about these Terms & Conditions, contact:{' '}
          <ProtectedEmail email="support@mynirva.com" />
        </SectionContent>
      </Box>
    </PolicyContentSection>
  );
};

export default ContentSection;
